import React, { useEffect, useRef, useState } from 'react';
import styles from './chat-message-item.module.scss';
import clsx from 'clsx';
import { Typography } from '@/ui-kit';
import { CheckMessageIcon, ErrorNotificationIcon, InfoIcon, NoCheckMessageIcon, PonytailIcon } from '@/assets/icons';
import { EDITING_FINISHED, REPLY_FINISHED, useChat, REPLY_MODE } from '@/store/useChat';
import { UAParser } from 'ua-parser-js';
import { format, differenceInMinutes } from 'date-fns';
import { useSpring, animated } from '@react-spring/web'
import { useDrag } from '@use-gesture/react';
import { Profile } from '@/types/profile';
import { useToast } from '@/store/useToast';
import WebApp from '@twa-dev/sdk';
import { useVibration } from '@/store/useVibration';

interface Props {
    isMyMessage?: boolean;
    message?: string;
    status?: boolean;
    datetime: Date;
    id?: number | string; // Добавляем ID сообщения
    editedAt: string | null;
    replyMsgId: number | null;
    replyMsgContent: string | null;
    hasReply: boolean;
    unsafe_message: boolean;
    profileChat: Profile | undefined,
    onMessageRead?: (id: number | string) => void; // Добавляем колбэк для отметки прочитанного
}
export const ChatMessageItem: React.FC<Props> = ({
    isMyMessage,
    message,
    status,
    datetime,
    id,
    editedAt,
    replyMsgId,
    replyMsgContent,
    hasReply,
    profileChat,
    onMessageRead,
    unsafe_message,
}) => {
    const parser = new UAParser();
    const device = parser.getDevice();
    const messageRef = useRef<HTMLDivElement>(null);
    const messageBubbleRef = useRef<HTMLDivElement>(null);
    const [isEdited, setIsEdited] = useState<boolean>(false);

    const showToast = useToast((state) => state.show);
    useEffect(() => setIsEdited(editedAt !== null), []);
    const chatMode = useChat((state) => state.chatMode);
    const setMenu = useChat((state) => state.setMenu);
    const setContextMsg = useChat((state) => state.setContextMsg);
    const contextMsg = useChat((state) => state.contextMsg);
    const chatStatus = useChat((state) => state.status);
    const setStatus = useChat((state) => state.setStatus);

    const isIncome = !isMyMessage
    const isUnreaded = !status
    const isSentByLast15Mins = differenceInMinutes(new Date(), datetime) <= 15
    const isNotReplied = !contextMsg?.has_reply

    useEffect(() => {
        if (isMyMessage || !id || !onMessageRead || !messageRef.current) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && entry.intersectionRatio === 1) {
                    if (!status) onMessageRead(id);
                    observer.unobserve(entry.target); // Прекращаем наблюдение
                }
            },
            {
                threshold: 1.0,
            },
        );

        observer.observe(messageRef.current);

        return () => {
            if (messageRef.current) {
                observer.unobserve(messageRef.current);
            }
        };
    }, [id, isMyMessage, onMessageRead]);

    useEffect(() => {
        if (
            id === contextMsg?.id &&
            chatStatus === EDITING_FINISHED
        ) {
            setIsEdited(true)
            setContextMsg(null);
            setStatus(null);
        } else if (
            id === contextMsg?.id &&
            chatStatus === REPLY_FINISHED
        ) {
            setContextMsg(null);
            setStatus(null);
        }
    }, [chatStatus]);

    const setChatMode = useChat((state) => state.setChatMode);
    const [{ x }, api] = useSpring(() => ({
        x: 0,
        scale: 1,
    }))

    const [lastTouch, setLastTouch] = useState<number | null>(null)
    const [isRunReplyModeAfterTouchUp, setRunReplyModeAfterTouchUp] = useState(false)

    const { isVibrationEnabled } =
        useVibration(state => state);

    return (
        <animated.div
            style={{ translate: x }}
            ref={messageRef}
            data-message-id={id}
            className={
                clsx(
                    isMyMessage ?
                        styles.rowSent
                    : styles.rowAccepted,
                    isMyMessage ?
                        'msg-row-sent'
                    : 'msg-row-accepted',
                    styles.row,
                )
            }
            onContextMenu={(e) => e.preventDefault()}
            onTouchStart={(e) => {
                if (!isMyMessage) {
                    setLastTouch(e.touches[0].clientX)
                    api.start({ x: 0 })
                }
            }}
            onTouchMove={(e) => {
                if (
                    lastTouch &&
                    messageBubbleRef
                        .current &&
                    !isMyMessage
                ) {
                    let val =
                        x.get() -
                        (
                            lastTouch -
                            e
                                .touches[0]
                                .clientX
                        ) * 5
                    if (val > 0)
                        val = 0
                    api.start({ x: val })
                    setLastTouch(e.touches[0].clientX)
                    console.log(`swipe touch x: ${x.get()}`)
                    if (x.get() < 0) {
                        if (!isRunReplyModeAfterTouchUp) {
                            setRunReplyModeAfterTouchUp(true)
                            if (isVibrationEnabled) {
                                WebApp
                                    .HapticFeedback
                                    .impactOccurred('medium');
                                // pass for not android
                                navigator.vibrate?.(10)
                            }
                        }
                    } else {
                        setRunReplyModeAfterTouchUp(false)
                    }
                }
            }}
            onTouchEnd={() => {
                if (!isMyMessage) {
                    setLastTouch(null)
                    api.start({ x: 0 })
                    if (
                        id &&
                        messageRef.current &&
                        isRunReplyModeAfterTouchUp
                    ) {
                        const bBox = messageRef.current?.getBoundingClientRect()
                        setContextMsg({
                            id,
                            content: message ?? '',
                            is_mine: isMyMessage,
                            status: status,
                            created_at: datetime,
                            pos: {
                                top:
                                    bBox.y +
                                    bBox.height +
                                    8 +
                                    120 >
                                    (
                                        document
                                            .querySelector('#chat-send-form')
                                            ?.getBoundingClientRect()
                                            .y ?? 0
                                    ) ?
                                        null
                                    :
                                        bBox.y + bBox.height + 8,
                                bottom: bBox.y +
                                    bBox.height +
                                    8 +
                                    120 >
                                    (
                                        document
                                            .querySelector('#chat-send-form')
                                            ?.getBoundingClientRect()
                                            .y ?? 0
                                    ) ?
                                        window.innerHeight -
                                        bBox.y +
                                        8
                                    :
                                        null,
                            },
                            reply_message_id: replyMsgId,
                            has_reply: hasReply,
                        })
                        setChatMode(REPLY_MODE)
                    }
                    setRunReplyModeAfterTouchUp(false)
                }
            }}>
            <div
                ref={messageBubbleRef}
                className={styles.message}
                onMouseUp={(e) => {
                    if (
                        device.type !== 'mobile' &&
                        chatMode === null &&
                        (
                            (
                                isMyMessage &&
                                isSentByLast15Mins &&
                                isNotReplied
                            ) ||
                            isIncome
                        ) &&
                        id &&
                        messageBubbleRef.current
                    ) {
                        setMenu(id)
                        const bBox =
                            messageBubbleRef
                                .current
                                ?.getBoundingClientRect()
                        setContextMsg({
                            id,
                            content: message ?? '',
                            is_mine: isMyMessage,
                            status: status,
                            created_at: datetime,
                            pos: {
                                top:
                                    bBox.y +
                                    bBox.height +
                                    8 +
                                    120 >
                                    (
                                        document
                                            .querySelector('#chat-send-form')
                                            ?.getBoundingClientRect()
                                            .y ?? 0
                                    ) ?
                                        null
                                    :
                                        bBox.y + bBox.height + 8,
                                bottom: bBox.y +
                                    bBox.height +
                                    8 +
                                    120 >
                                    (
                                        document
                                            .querySelector('#chat-send-form')
                                            ?.getBoundingClientRect()
                                            .y ?? 0
                                    ) ?
                                        window.innerHeight -
                                        bBox.y +
                                        8
                                    :
                                        null,
                            },
                            reply_message_id: replyMsgId,
                            has_reply: hasReply,
                        })
                    }
                }}
                onClick={(e) => {
                    if (
                        device.type === 'mobile' &&
                        chatMode === null &&
                        (
                            (
                                isMyMessage &&
                                isSentByLast15Mins &&
                                isNotReplied
                            ) ||
                            isIncome
                        ) &&
                        id &&
                        messageBubbleRef.current
                    ) {
                        setMenu(id)
                        const bBox =
                            messageBubbleRef
                                .current
                                ?.getBoundingClientRect()
                        setContextMsg({
                            id,
                            content: message ?? '',
                            is_mine: isMyMessage,
                            status: status,
                            created_at: datetime,
                            pos: {
                                top:
                                    bBox.y +
                                    bBox.height +
                                    8 +
                                    120 >
                                    (
                                        document
                                            .querySelector('#chat-send-form')
                                            ?.getBoundingClientRect()
                                            .y ?? 0
                                    ) ?
                                        null
                                    :
                                        bBox.y + bBox.height + 8,
                                bottom: bBox.y +
                                    bBox.height +
                                    8 +
                                    120 >
                                    (
                                        document
                                            .querySelector('#chat-send-form')
                                            ?.getBoundingClientRect()
                                            .y ?? 0
                                    ) ?
                                        window.innerHeight -
                                        bBox.y +
                                        8
                                    :
                                        null,
                            },
                            reply_message_id: replyMsgId,
                            has_reply: hasReply,
                        })
                    }
                }}>
                <Typography
                    typeColor="black"
                    variant="p"
                    className={styles.messageText}>
                    <div className={styles.msgContent}>
                        {
                            replyMsgId && replyMsgContent ?
                                <div className={styles.replyMsgHeader}>
                                    <div className={styles.replyMsgHeaderContent}>
                                        <span className={styles.replyMsgHeaderContentTitle}>
                                            {profileChat?.public_name}
                                        </span>
                                        <p className={styles.replyMsgHeaderContentSubtitle}>
                                            {replyMsgContent}
                                        </p>
                                    </div>
                                </div>
                            :
                                <></>
                        }
                        {message}
                    </div>
                    <PonytailIcon className={styles.dekorIcon} />
                </Typography>
                <div className={styles.msgFooter}>
                    {
                        isEdited ?
                            <Typography
                                typeColor="gray"
                                className={styles.messageEditedLabel}
                                variant="span">
                                ✏️ ред.
                            </Typography>
                        :
                            <></>
                    }
                    <Typography
                        typeColor="gray"
                        className={
                            clsx(
                                styles.messageTime,
                                isMyMessage && styles['messageTime--outcome']
                            )
                        }
                        variant="span">
                        {format(datetime, 'HH:mm')}
                    </Typography>
                    {isMyMessage && status && (
                        <CheckMessageIcon
                            width={12}
                            height={12}
                            className={styles.messageIcon} />
                    )}
                    {isMyMessage && isUnreaded && (
                        <NoCheckMessageIcon
                            width={12}
                            height={12}
                            className={styles.messageIcon} />
                    )}
                </div>
            </div>
        </animated.div>
    )
}
